'use client';
import React, { useState, useEffect } from 'react';
import SectionTitle from './SectionTitle';
import RelatedCaseStudiesCard from './RelatedCaseStudiesCard';
import { caseStudies } from '@/data/caseStudies';

interface RelatedCaseStudiesProps {
  title?: string;
  subtitle?: string;
  description?: string;
  align?: "left" | "center" | "right";
  maxItems?: number;
  excludeCurrent?: string; // Company name to exclude from the list
  currentCaseStudy?: string; // Alternative prop name for current case study
}

const RelatedCaseStudies: React.FC<RelatedCaseStudiesProps> = ({
  title = "Related",
  subtitle = "Case Studies",
  description = "Explore our portfolio of successful projects and see how we've helped businesses achieve their goals through innovative design and development solutions.",
  align = "left",
  maxItems = 2,
  excludeCurrent,
  currentCaseStudy
}) => {
  // Determine which case study to exclude
  const caseStudyToExclude = excludeCurrent || currentCaseStudy;
  
  // Filter out the current case study
  const filteredCaseStudies = caseStudies.filter(
    study => study.company !== caseStudyToExclude
  );

  // Initialize with first N items (deterministic for SSR)
  const [displayCaseStudies, setDisplayCaseStudies] = useState(
    filteredCaseStudies.slice(0, maxItems)
  );

  // Shuffle only on the client side after mount to avoid hydration mismatch
  useEffect(() => {
    const shuffled = [...filteredCaseStudies].sort(() => Math.random() - 0.5);
    setDisplayCaseStudies(shuffled.slice(0, maxItems));
  }, [caseStudyToExclude, maxItems]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="py-12 ">
      <div className="custom-container">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 lg:gap-12">
          {/* Left Section - Title */}
          <div className="lg:w-1/3">
            <SectionTitle
              title={title}
              subtitle={subtitle}
              description={description}
              align={align}
              titleMaxWidth="sm"
              descriptionMaxWidth="lg"
            />
          </div>

          {/* Right Section - Case Study Cards */}
          <div className="lg:w-2/3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {displayCaseStudies.map((caseStudy) => (
                <RelatedCaseStudiesCard
                  key={caseStudy.company}
                  thumbnailSrc={caseStudy.thumbnailSrc || '/test-images/default-thumbnail.png'}
                  company={caseStudy.company}
                  description={caseStudy.description}
                  path={caseStudy.path}
                  logoSrc={caseStudy.logoSrc}
                  tags={caseStudy.tags}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RelatedCaseStudies;
